import { STATSIG_EVENTS } from "@repo/statsig";
import Image from "next/image";
import contactSupportIcon from "public/icons/ContactSupportIcon.svg";
import manageSubscriptionIcon from "public/icons/ManageSubscriptionIcon.svg";
import termsIcon from "public/icons/TermsIcon.svg";
import React, { useEffect, useState } from "react";
import { BodyTitleText } from "src/components/KinsaTypography";
import { TOS_ROOT } from "src/lib/routes";
import PRODUCT_NAME, { SUPPORT_EMAIL } from "src/lib/product-name";
import { handleManageSubscription } from "../actions";
import { MenuButton } from "./menu-button";

interface ContentProps {
  headerText: string;
  className?: string;
  onClose?: () => void;
  hasSubscription: boolean;
  onSeePlans: () => void;
}

const Content: React.FC<ContentProps> = ({
  headerText,
  className,
  onClose,
  hasSubscription,
  onSeePlans,
}) => {
  const [isLoading, setIsLoading] = useState(hasSubscription);
  const [subscriptionUrl, setSubscriptionUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hasSubscription) return;

    const fetchSubscriptionUrl = async () => {
      try {
        const result = await handleManageSubscription();
        if ("error" in result) {
          console.error("Error fetching subscription URL:", result.error);
          return;
        }
        setSubscriptionUrl(result.url);
      } catch (error) {
        console.error("Error fetching subscription URL:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionUrl();
  }, [hasSubscription]);

  if (isLoading) {
    return (
      <div
        className={`flex flex-col items-center justify-center py-12 ${className}`}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex flex-row gap-[8px] items-center justify-start">
        <BodyTitleText className="leading-loose">{headerText}</BodyTitleText>
      </div>
      <div className="flex flex-col">
        <MenuButton
          icon={
            <Image
              src={manageSubscriptionIcon}
              alt=""
              width={16}
              height={14.22}
              unoptimized
            />
          }
          text={hasSubscription ? "Manage subscription" : "See Plans"}
          href={subscriptionUrl ?? undefined}
          onClick={
            subscriptionUrl
              ? onClose
              : () => {
                  onClose?.();
                  onSeePlans();
                }
          }
          disabled={isLoading || (!subscriptionUrl && !onSeePlans)}
          statsigEvent={STATSIG_EVENTS.MANAGE_SUBSCRIPTION}
        />
        <div className="border-b border-foreground-muted/20"></div>
        <MenuButton
          icon={
            <Image src={termsIcon} alt="" width={12} height={16} unoptimized />
          }
          text="Terms and conditions"
          onClick={onClose}
          href={TOS_ROOT}
          statsigEvent={STATSIG_EVENTS.TERMS_CONDITIONS}
        />
        <div className="border-b border-foreground-muted/20"></div>
        <MenuButton
          icon={
            <Image
              src={contactSupportIcon}
              alt=""
              width={16}
              height={16}
              unoptimized
            />
          }
          text={`Contact ${PRODUCT_NAME} Support`}
          href={`mailto:${SUPPORT_EMAIL}`}
          onClick={onClose}
          statsigEvent={STATSIG_EVENTS.CONTACT_SUPPORT}
        />
      </div>
    </div>
  );
};

export default Content;
